/**
 * RESET PIN. Module 01, screen 14 (FR-U-ID-011).
 *
 * Identity re-check, then a new PIN. Three steps: confirm the phone, verify a one-time code, set a new
 * PIN. Like sign in, the outcome messaging is deliberately non-committal about whether an account
 * exists, so this is not an enumeration oracle. UI-first: the code path is simulated.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Brandmark } from '@/components/brand';
import { OtpField, PinField } from '@/components/auth-fields';
import { ThemeToggle } from '@/components/theme-toggle';

type Step = 'identify' | 'verify' | 'reset' | 'done';

export default function ResetPin() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('identify');
  const [phone, setPhone] = useState('+2547');
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function fakeStep(next: Step) {
    setBusy(true);
    setError(null);
    await new Promise((r) => setTimeout(r, 500));
    setBusy(false);
    setStep(next);
  }

  return (
    <main className="flex min-h-[100svh] flex-col px-4 py-8">
      <div className="flex items-center justify-between">
        <Link to="/signin" className="text-sm text-ink-muted hover:text-ink">
          &larr; Sign in
        </Link>
        <ThemeToggle />
      </div>

      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
        <header className="mb-8 flex flex-col items-center text-center">
          <Brandmark size="lg" />
          <h1 className="mt-4 text-page-title text-ink">Reset your PIN</h1>
          <p className="mt-1 text-sm text-ink-muted">
            {step === 'identify' && 'Confirm your phone number to begin.'}
            {step === 'verify' && `Enter the code we sent to ${phone}.`}
            {step === 'reset' && 'Choose a new PIN.'}
            {step === 'done' && 'Your PIN has been reset.'}
          </p>
        </header>

        <div className="rounded-xl border border-border bg-surface p-6 shadow-card">
          {error ? (
            <div
              role="alert"
              className="mb-4 rounded-field border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
            >
              {error}
            </div>
          ) : null}

          {step === 'identify' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void fakeStep('verify');
              }}
              className="space-y-5"
            >
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-ink">
                  Phone number
                </label>
                <input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                  required
                  className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2.5 font-mono text-base text-ink outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
                />
              </div>
              <SubmitButton busy={busy}>Send code</SubmitButton>
            </form>
          ) : null}

          {step === 'verify' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void fakeStep('reset');
              }}
              className="space-y-5"
            >
              <OtpField value={code} onChange={setCode} autoFocus />
              <button
                type="button"
                className="mx-auto block text-xs font-medium text-brand hover:underline"
              >
                Resend code
              </button>
              <SubmitButton busy={busy} disabled={code.length < 6}>
                Verify
              </SubmitButton>
            </form>
          ) : null}

          {step === 'reset' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (pin !== confirm) {
                  setError('Those two PINs do not match.');
                  return;
                }
                void fakeStep('done');
              }}
              className="space-y-5"
            >
              <div>
                <p className="mb-2 text-sm font-medium text-ink">New PIN</p>
                <PinField value={pin} onChange={setPin} length={4} autoFocus confirm />
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-ink">Confirm new PIN</p>
                <PinField
                  value={confirm}
                  onChange={setConfirm}
                  length={4}
                  confirm
                  invalid={!!error}
                />
              </div>
              <SubmitButton busy={busy} disabled={pin.length < 4 || confirm.length < 4}>
                Set new PIN
              </SubmitButton>
            </form>
          ) : null}

          {step === 'done' ? (
            <div className="text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="m5 13 4 4L19 7"
                  />
                </svg>
              </span>
              <p className="mt-4 text-sm text-ink-muted">
                All sessions were signed out for your security. Sign in with your new PIN.
              </p>
              <button
                onClick={() => navigate('/signin')}
                className="mt-5 w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light"
              >
                Go to sign in
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </main>
  );
}

function SubmitButton({
  children,
  busy,
  disabled,
}: {
  children: React.ReactNode;
  busy: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
    >
      {busy ? 'Please wait...' : children}
    </button>
  );
}
